import React, { useState, useRef, useEffect } from 'react';
import { X, Sparkles, User, Bot, Send, Loader2, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AIChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  initialPrompt?: string | undefined;
  context?: string; // e.g., "Risk Assessment", "Compliance Dashboard"
}

/**
 * AIChatPanel - Right-side AI conversation panel (like GitHub Copilot Chat)
 * 
 * Slides in from the right when user interacts with AI prompt input.
 * Provides conversational AI interface for complex actions and guidance.
 */
export function AIChatPanel({ isOpen, onClose, initialPrompt, context }: AIChatPanelProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastInitialPromptRef = useRef<string | undefined>(undefined);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens and handle initial prompt
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      
      // If there's a new initial prompt (different from last one), clear and send
      if (initialPrompt && initialPrompt !== lastInitialPromptRef.current) {
        lastInitialPromptRef.current = initialPrompt;
        setMessages([]); // Clear existing conversation
        handleSendMessage(initialPrompt);
      }
    }
  }, [isOpen, initialPrompt]);

  // Mock AI response (replace with actual API call)
  const getMockAIResponse = (userPrompt: string): string => {
    const lowerPrompt = userPrompt.toLowerCase();
    
    if (lowerPrompt.includes('create') || lowerPrompt.includes('add')) {
      return `I'll help you create that. Based on your request, I'll need a few details:\n\n1. What would you like to name it?\n2. Which category or section should it belong to?\n3. Are there any specific requirements or constraints?\n\nI can guide you through the process step-by-step, or if you prefer, you can provide all the details at once and I'll set it up for you.`;
    }
    
    if (lowerPrompt.includes('risk') || lowerPrompt.includes('assess')) {
      return `I can help you with risk assessment. Here are some things I can do:\n\n• Analyze existing risks and suggest mitigation strategies\n• Create new risk entries with calculated severity scores\n• Link risks to controls and business processes\n• Generate risk reports and visualizations\n\nWhat would you like to focus on?`;
    }
    
    if (lowerPrompt.includes('report') || lowerPrompt.includes('export')) {
      return `I can generate various reports for you:\n\n• Compliance status reports (DORA, ISO 27001, NIST)\n• Risk register summaries\n• Control effectiveness dashboards\n• Audit trail exports\n\nWhich type of report would you like, and for what time period?`;
    }
    
    if (lowerPrompt.includes('help') || lowerPrompt.includes('how')) {
      return `I'm Themis, your AI assistant for ${context || 'Line4 Observability'}. I can help you:\n\n✨ Create and manage risk data\n🔍 Analyze trends and patterns\n📊 Generate reports and visualizations\n🎯 Suggest best practices and improvements\n🔗 Connect related items (risks, controls, processes)\n\nJust tell me what you'd like to do in plain English, and I'll guide you through it!`;
    }
    
    return `I understand you'd like to: "${userPrompt}"\n\nLet me help you with that. Could you provide a bit more detail about what you're trying to achieve? The more context you give me, the better I can assist you.`;
  };

  const handleSendMessage = async (messageContent?: string) => {
    const content = messageContent || inputValue.trim();
    if (!content) return;

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Simulate AI thinking time
    await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 700));

    // Add AI response
    const aiMessage: Message = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: getMockAIResponse(content),
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, aiMessage]);
    setIsLoading(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSendMessage();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Chat Panel Content - No positioning, parent handles it */}
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-800 bg-neutral-900">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-gradient-to-r from-primary-500 to-cyan-500">
              <Sparkles className="w-4 h-4 text-black" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Themis</h3>
              {context && (
                <p className="text-xs text-neutral-400">{context}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setMessages([])}
              className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors"
              title="New Chat"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="p-4 rounded-full bg-gradient-to-r from-primary-500/20 to-cyan-500/20 mb-4">
                <Sparkles className="w-8 h-8 text-primary-500" />
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">
                How can I help you today?
              </h4>
              <p className="text-sm text-neutral-400 mb-4">
                Ask me anything about {context || 'risk management, compliance, or controls'}.
              </p>
              <div className="space-y-2 w-full">
                <button
                  onClick={() => handleSendMessage("Help me create a new risk assessment")}
                  className="w-full text-left px-4 py-2 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-sm text-neutral-300 transition-colors"
                >
                  Help me create a new risk assessment
                </button>
                <button
                  onClick={() => handleSendMessage("Show me compliance gaps")}
                  className="w-full text-left px-4 py-2 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-sm text-neutral-300 transition-colors"
                >
                  Show me compliance gaps
                </button>
                <button
                  onClick={() => handleSendMessage("Generate a risk report")}
                  className="w-full text-left px-4 py-2 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-sm text-neutral-300 transition-colors"
                >
                  Generate a risk report
                </button>
              </div>
            </div>
          )}

          {messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                "flex gap-3",
                message.role === 'user' ? "justify-end" : "justify-start"
              )}
            >
              {message.role === 'assistant' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-r from-primary-500 to-cyan-500 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-black" />
                </div>
              )}
              
              <div
                className={cn(
                  "max-w-[80%] px-4 py-2.5 rounded-lg whitespace-pre-wrap",
                  message.role === 'user'
                    ? "bg-primary-500 text-black"
                    : "bg-neutral-900 text-white border border-neutral-800"
                )}
              >
                <p className="text-sm leading-relaxed">{message.content}</p>
              </div>

              {message.role === 'user' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-neutral-800 flex items-center justify-center">
                  <User className="w-5 h-5 text-neutral-400" />
                </div>
              )}
            </div>
          ))}

          {isLoading && (
            <div className="flex gap-3 justify-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-r from-primary-500 to-cyan-500 flex items-center justify-center">
                <Bot className="w-5 h-5 text-black" />
              </div>
              <div className="px-4 py-2.5 rounded-lg bg-neutral-900 border border-neutral-800">
                <Loader2 className="w-5 h-5 text-primary-500 animate-spin" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-neutral-800 bg-neutral-900">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 px-4 py-2.5 rounded-lg bg-neutral-950 border border-neutral-700 text-white placeholder-neutral-500 focus:outline-none focus:border-primary-500 transition-colors"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className={cn(
                "p-2.5 rounded-lg transition-colors",
                inputValue.trim() && !isLoading
                  ? "bg-primary-500 text-black hover:bg-primary-600"
                  : "bg-neutral-800 text-neutral-600 cursor-not-allowed"
              )}
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      </div>
    </>
  );
}